import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { mutate } from 'swr';

import {
  agentSessionKey,
  agentSessionTurnsKey,
  answerAgentSession,
  updateAgentSessionModel,
  type AgentSession,
  type AgentSessionActivity,
  type AgentSessionAskResponse,
  useAgentSessionActivity,
  useAgentSessionTurns,
} from '@/api-models/agent-sessions';

import { useAgentSessionSocket } from '../hooks/useAgentSessionSocket';
import { useMessageQuote } from '../hooks/useMessageQuote';
import { selectGoal, useAgentSessionStore, type AgentTurn } from '../stores/agent-session-store';
import { type GoalCommand, type GoalStartRequest, parseGoalCommand } from '../stores/goal';

import { SelectionToolbar } from './SelectionToolbar';
import { sessionStatusLine } from './session-status';
import { ActivityRow } from './thread/ActivityRow';
import { ApprovalCard } from './thread/ApprovalCard';
import { AskUserCard } from './thread/AskUserCard';
import { BackgroundJobsIndicator } from './thread/BackgroundJobsIndicator';
import { Composer } from './thread/Composer';
import { GoalBanner } from './thread/GoalBanner';
import { SandboxStatusLine } from './thread/SandboxStatusLine';
import { StartGoalModal } from './thread/StartGoalModal';
import { TodoProgressIndicator } from './thread/TodoProgressIndicator';
import { TurnBubble } from './thread/TurnBubble';

type ThreadEntry = { kind: 'turn'; turn: AgentTurn } | { kind: 'activity'; activity: AgentSessionActivity };

/** Whose words a turn holds, as `useMessageQuote` reads them back — `undefined` for nobody's. */
const QUOTABLE_ROLE: Record<AgentTurn['role'], 'user' | 'assistant' | undefined> = {
  user: 'user',
  assistant: 'assistant',
  system: undefined,
  job: undefined,
  goal: undefined,
};

/** Sorts after any real ISO timestamp; stands in for "now" on an undated turn. */
const END_OF_TIME = '9999';

/**
 * Merge turns and activity rows into one time-ordered thread.
 *
 * "Woken up: CI failed" only makes sense sitting between the turn that pushed and
 * the turn that fixed it — appended at either end it reads as unrelated. Turns
 * keep their relative order regardless of timestamps (they are already in order,
 * and a live turn's client clock may disagree with the server's by a second or
 * two); activity rows are slotted into that sequence by `created_at`.
 *
 * Turns with no timestamp — nothing writes them today, but the field is optional —
 * sort as "now" so they stay at the end rather than jumping to the top.
 */
export function interleave(turns: AgentTurn[], activity: AgentSessionActivity[] | null): ThreadEntry[] {
  if (!activity || activity.length === 0) return turns.map((turn) => ({ kind: 'turn', turn }));

  const rows = [...activity].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.seq - b.seq);
  const out: ThreadEntry[] = [];
  let next = 0;

  for (const turn of turns) {
    // An undated turn is one born live, i.e. happening now — so every row already
    // written belongs before it. Sorting it as "now" rather than skipping the
    // flush is what keeps a just-arrived reply below the activity it followed.
    const at = turn.createdAt ?? END_OF_TIME;
    while (next < rows.length && rows[next]!.created_at <= at) {
      out.push({ kind: 'activity', activity: rows[next]! });
      next += 1;
    }
    out.push({ kind: 'turn', turn });
  }

  for (; next < rows.length; next += 1) out.push({ kind: 'activity', activity: rows[next]! });

  return out;
}

/**
 * The chat thread, mounted for the whole life of a session — including while the
 * sandbox is provisioning or paused.
 *
 * It streams over the WebSocket when the sandbox is running and otherwise renders
 * durable DB history (or, on a brand-new session, the initial prompt optimistically).
 * The composer stays usable throughout: sending on a paused session is what resumes it.
 *
 * `hidden` (the panel is maximized) is `display: none` rather than an unmount —
 * this component owns the agent socket, and dropping it would stop the run
 * streaming for as long as the panel stayed full-width.
 */
export function ThreadColumn({
  session,
  hidden,
  onSessionChanged,
  quotable = true,
}: {
  session: AgentSession;
  hidden: boolean;
  /** Refetch the session after the composer changes something on it (e.g. its model). */
  onSessionChanged: () => void;
  /**
   * Whether selecting text here offers to quote it into the next message.
   *
   * Off in the plan view. There, the document beside the thread is what the user is marking up,
   * and two panes both answering a drag with a popover makes it ambiguous which one a selection
   * belongs to — so the plan pane owns the gesture and the thread stays plain text.
   */
  quotable?: boolean;
}) {
  // Gated deliberately — opening this socket resumes the sandbox. A planning session has no
  // sandbox to resume, and its agent is bound the moment the socket opens, so it connects
  // unconditionally.
  const planning = session.mode === 'plan';
  const live = planning || session.status === 'running';
  const { sendPrompt, abort, respondToApproval, killJobs, startGoal, stopGoal, resumeGoal } = useAgentSessionSocket(
    session.id,
    live,
  );
  const goal = useAgentSessionStore(selectGoal);
  const [goalFormOpen, setGoalFormOpen] = useState(false);
  const { turns: dbTurns, isLoading: turnsLoading } = useAgentSessionTurns(session.id);
  const { activity } = useAgentSessionActivity(session.id);
  const turns = useAgentSessionStore((s) => s.turns);
  const turnInFlight = useAgentSessionStore((s) => s.turnInFlight);
  const connected = useAgentSessionStore((s) => s.connected);
  const attachState = useAgentSessionStore((s) => s.attachState);
  const attachDetail = useAgentSessionStore((s) => s.attachDetail);
  const threadEmpty = useAgentSessionStore((s) => s.turns.length === 0);
  const pendingAsk = useAgentSessionStore((s) => s.pendingAsk);
  const pendingApproval = useAgentSessionStore((s) => s.pendingApproval);
  const hydrate = useAgentSessionStore((s) => s.hydrate);
  const resolveAsk = useAgentSessionStore((s) => s.resolveAsk);
  const scrollRef = useRef<HTMLDivElement>(null);
  const quote = useMessageQuote(scrollRef);

  const status = sessionStatusLine(session);

  /**
   * Deliver answers to the question the agent stopped on.
   *
   * Always REST, never the chat socket: the agent's turn already ended, so there is nothing live
   * to steer, and the session may well be hibernated. Refetching the turns afterwards is what
   * retires the card — the answered tool call gains a result, which is exactly what
   * `derivePendingAsk` keys off.
   */
  async function answer(toolCallId: string, response: AgentSessionAskResponse): Promise<boolean> {
    try {
      const output = await answerAgentSession(session.id, toolCallId, response);
      // Retire the card here rather than waiting for a refetch: `hydrate` deliberately refuses to
      // clobber live turns, so the reloaded history would not reach the thread. Storing the JSON
      // the server rendered keeps this identical to what a later reload reads back.
      resolveAsk(toolCallId, output);
      // The session row carries `awaiting_user_input_at` and may have just resumed a sandbox.
      void mutate(agentSessionKey(session.id));
      void mutate(agentSessionTurnsKey(session.id));
      return true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't deliver that answer — your choices are still here.");
      return false;
    }
  }

  /**
   * A typed message while a question is pending *is* the answer to it — to the whole card, not to
   * whichever question comes first.
   *
   * Sending it as an ordinary prompt would leave the `ask_user` call dangling — the agent would
   * see "(no result recorded)" where its own question's answer belongs — and the message would
   * show up twice, once as a user turn and once as the tool result. The server routes a stray
   * prompt the same way, so the two paths agree whichever one a message takes.
   */
  function send(message: string, images: string[]): boolean {
    const goalCommand = parseGoalCommand(message);
    if (goalCommand) return runGoalCommand(goalCommand);
    if (!pendingAsk) return sendPrompt(message, images);
    if (images.length > 0) {
      toast.error('Answer the question first — screenshots travel with an ordinary message.');
      return false;
    }
    // Returned `true` before the request settles, so the composer clears the way it does for a
    // prompt. The failure path below re-states what was typed, since by then the draft is gone.
    void answer(pendingAsk.toolCallId, { reply: message }).then((ok) => {
      if (!ok) toast.error(`That answer didn't go through. You wrote: “${message}”`);
    });
    return true;
  }

  /**
   * A typed `/goal …` goes to the loop as a goal command, never to the model as a prompt. A goal
   * started from the composer runs unattended, like one started from the form with "Ask me when
   * unsure" left off.
   */
  function runGoalCommand(command: GoalCommand): boolean {
    switch (command.kind) {
      case 'invalid':
        toast.error(command.message);
        return false;
      case 'start':
        return reportGoalSent(
          startGoal({
            objective: command.objective,
            budgetMicroUsd: command.budgetMicroUsd,
            maxIterations: command.maxIterations,
            interactive: false,
          }),
        );
      case 'stop':
        return reportGoalSent(stopGoal());
      case 'resume':
        return reportGoalSent(resumeGoal());
    }
  }

  /** `sent`, after telling the user why a goal frame could not go out. */
  function reportGoalSent(sent: boolean): boolean {
    if (!sent) toast.error('Goals need a live connection to the sandbox — wait for it to finish starting.');
    return sent;
  }

  // Switching a *live* session's model needs no re-provision: the proxy reads
  // `agent_session.model` on every forwarded call, so it lands on the agent's next one.
  async function selectModel(id: string) {
    if (id === session.model) return;
    try {
      await updateAgentSessionModel(session.id, id);
      onSessionChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to change model');
    }
  }

  // The socket can be open while the sandbox behind it is still resuming (the upgrade no longer
  // waits on Daytona), so "can I actually talk to the agent" is `connected && ready` — not just
  // `connected`. Without surfacing this, a stalled attach is indistinguishable from a thinking
  // agent, which is exactly how a broken connection used to read as an endless spinner.
  const attached = live && connected && attachState === 'ready';
  const attachHint = !live
    ? null
    : !connected
      ? 'Reconnecting to the agent…'
      : attachState === 'resuming'
        ? `Bringing the sandbox back online${attachDetail ? ` (${attachDetail})` : ''}…`
        : attachState === 'unavailable'
          ? attachDetail || 'The agent is unavailable right now — retrying…'
          : null;
  // `attachHint` only outranks the status line in the `ready` phase, which a plan session is
  // never in — so surface it here instead, or a failed planning attach shows nothing at all.
  const planningHint = planning && !attached ? attachHint : null;

  // Seed the thread from durable history so a reload shows the conversation
  // immediately, independent of the live socket. With no history yet, fall back
  // to the session's initial prompt so a session created seconds ago already
  // shows what the user asked for. Keyed on `threadEmpty` so it re-seeds after a
  // reset but not once per streamed token; waits for the fetch so a brand-new
  // session doesn't flash the prompt and then re-render.
  useEffect(() => {
    if (turnsLoading) return;
    hydrate({
      history: dbTurns,
      initialPrompt: session.initial_prompt,
      initialImages: session.initial_images,
    });
  }, [turnsLoading, dbTurns, session.initial_prompt, session.initial_images, threadEmpty, hydrate]);

  // Keyed on `hidden` as well as `turns`: while hidden the element has no box, so
  // `scrollHeight` reads 0 and every turn that streams in parks the thread at the
  // top. Re-running on reveal puts it back on the newest turn.
  useEffect(() => {
    if (hidden) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    // `pendingAsk` too: the card claims height below the thread, so a question arriving without
    // this leaves the message that motivated it pushed out of view.
  }, [turns, hidden, pendingAsk]);

  const thread = interleave(turns, activity);

  return (
    <div style={{ display: hidden ? 'none' : 'flex' }} className="flex min-h-0 min-w-0 flex-1 flex-col bg-surface-1">
      {goal && <GoalBanner goal={goal} onStop={() => void stopGoal()} onResume={() => void resumeGoal()} />}
      <TodoProgressIndicator />
      <div
        ref={scrollRef}
        onMouseUp={quotable ? quote.onMouseUp : undefined}
        // `quotable-selection` styles the selection as the staging affordance it is, and matches
        // the repaint that keeps it visible while the popover's note field holds focus.
        className={`relative flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-6 ${quotable ? 'quotable-selection' : ''}`}
      >
        {turns.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-sm text-fg-tertiary">
            {planning
              ? 'Describe what you want to build and the agent will draft a plan.'
              : 'Ask the agent to get started.'}
          </div>
        ) : (
          thread.map((entry) =>
            entry.kind === 'activity' ? (
              // No `data-turn-role`: an activity row is not anybody's words, so
              // `useMessageQuote` must not offer to quote it — same reason system
              // notices carry none.
              <div key={`activity-${entry.activity.id}`}>
                <ActivityRow activity={entry.activity} />
              </div>
            ) : (
              // The role marker is what `useMessageQuote` reads back off a text selection to
              // work out whose words were picked; system notices and job reports are nobody's
              // words, so they carry none and aren't quotable.
              <div key={entry.turn.id} data-turn-role={QUOTABLE_ROLE[entry.turn.role]}>
                <TurnBubble turn={entry.turn} />
              </div>
            ),
          )
        )}
        {quotable && quote.popup && (
          <SelectionToolbar
            ref={quote.popoverRef}
            style={{ left: quote.popup.x, top: quote.popup.y }}
            onAdd={quote.attach}
            onCancel={quote.dismiss}
          />
        )}
      </div>
      {pendingAsk && (
        <AskUserCard
          // Remount on a new question so no draft survives from the previous one.
          key={pendingAsk.toolCallId}
          ask={pendingAsk}
          disabled={!status.canSend}
          onSubmit={(answers) => answer(pendingAsk.toolCallId, { answers })}
        />
      )}
      {pendingApproval && <ApprovalCard approval={pendingApproval} onRespond={respondToApproval} />}
      <BackgroundJobsIndicator killJobs={killJobs} />
      <Composer
        sessionId={session.id}
        turnInFlight={turnInFlight}
        onSend={send}
        onAbort={abort}
        sessionModel={session.model}
        onSelectModel={(id) => void selectModel(id)}
        onStartGoal={planning ? undefined : () => setGoalFormOpen(true)}
        // Only a failed sandbox blocks input. Everything else routes the prompt
        // over REST, which resumes the sandbox on the way through. A pending approval also
        // blocks it: the turn is stopped on that question, so a prompt sent now would queue
        // behind it and read as being ignored. A pending `ask_user` does not block — answering
        // in the composer instead of the card is a legitimate way to reply — so it only
        // relabels the placeholder.
        disabled={!status.canSend || pendingApproval !== null}
        placeholder={pendingAsk ? 'Answer above, or reply here instead…' : undefined}
        statusLine={
          <SandboxStatusLine session={session} attachHint={planning ? planningHint : attached ? null : attachHint} />
        }
      />
      {goalFormOpen && (
        <StartGoalModal
          onClose={() => setGoalFormOpen(false)}
          onStart={(request: GoalStartRequest) => reportGoalSent(startGoal(request))}
        />
      )}
    </div>
  );
}
